import React, { useState, useEffect } from "react";
import { Card, Tabs, Table, InputNumber, Button, Switch, message, Row, Col, Statistic, Divider } from "antd";
import axios from "axios";

const TransportModel = ({ fileId, fileName, setSelectedFile, initialConfig }) => {
    const [activeTab, setActiveTab] = useState("input");
    const [loading, setLoading] = useState(false);
    const [periods, setPeriods] = useState(initialConfig?.periods || 3);
    const [isSaved, setIsSaved] = useState(false);

    // Input State
    const [inputs, setInputs] = useState({
        shortage_allowed: false,
        backorder_cost: 0,
        holding_cost: 0,
        idle_cost: 0,
        init_inventory: 0,
        demand: Array(periods).fill(0),
        reg_cap: Array(periods).fill(0),
        ot_cap: Array(periods).fill(0),
        sub_cap: Array(periods).fill(0),
        reg_cost: Array(periods).fill(0),
        ot_cost: Array(periods).fill(0),
        sub_cost: Array(periods).fill(0),
    });

    const [output, setOutput] = useState(null);

    useEffect(() => {
        if (fileId) loadData();
    }, [fileId]);

    const loadData = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`/api/retrieve_transportation_model/${fileId}/`, {
                headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` }
            });
            setInputs(res.data.input_data);
            setPeriods(res.data.input_data.periods);
            setOutput(res.data.output_data);
            setIsSaved(true);
        } catch (err) {
            if (err.response?.status === 401) window.location.href = "/login";
            message.error("Failed to load data");
        } finally { setLoading(false); }
    };

    const handleCalculate = async () => {
        try {
            setLoading(true);
            const payload = { name: fileName, input_data: { ...inputs, periods, title: fileName } };
            const url = fileId ? `/api/update_transportation_model/${fileId}/` : "/api/save_transportation_model/";
            const method = fileId ? "put" : "post";

            const res = await axios[method](url, payload, {
                headers: { Authorization: `Bearer ${localStorage.getItem("access_token")}` }
            });

            setOutput(res.data.output_data);
            setIsSaved(true);
            setActiveTab("output");
            if (!fileId) setSelectedFile({ ...res.data, type: "transportation_model" });
            message.success("Calculation Successful");
        } catch (err) {
            message.error(err.response?.data?.error || "Calculation failed");
        } finally { setLoading(false); }
    };

    const updateArray = (key, index, value) => {
        const newArr = [...inputs[key]];
        newArr[index] = value;
        setInputs({ ...inputs, [key]: newArr });
    };

    const inputColumns = [
        { title: "Period", dataIndex: "period", width: 80, fixed: 'left' },
        { title: "Demand", render: (_, __, i) => <InputNumber value={inputs.demand[i]} onChange={v => updateArray("demand", i, v)} /> },
        { title: "Reg. Cap", render: (_, __, i) => <InputNumber value={inputs.reg_cap[i]} onChange={v => updateArray("reg_cap", i, v)} /> },
        { title: "OT Cap", render: (_, __, i) => <InputNumber value={inputs.ot_cap[i]} onChange={v => updateArray("ot_cap", i, v)} /> },
        { title: "Sub. Cap", render: (_, __, i) => <InputNumber value={inputs.sub_cap[i]} onChange={v => updateArray("sub_cap", i, v)} /> },
        { title: "Reg. Cost", render: (_, __, i) => <InputNumber value={inputs.reg_cost[i]} onChange={v => updateArray("reg_cost", i, v)} /> },
        { title: "OT Cost", render: (_, __, i) => <InputNumber value={inputs.ot_cost[i]} onChange={v => updateArray("ot_cost", i, v)} /> },
        { title: "Sub. Cost", render: (_, __, i) => <InputNumber value={inputs.sub_cost[i]} onChange={v => updateArray("sub_cost", i, v)} /> },
    ];

    return (
        <Card title={`Transportation Model: ${fileName}`}>
            <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
                {
                    key: "input", label: "Configuration", children: (
                        <div>
                            <Row gutter={16} style={{ marginBottom: 20 }}>
                                <Col span={6}>Holding Cost: <InputNumber value={inputs.holding_cost} onChange={v => setInputs({ ...inputs, holding_cost: v })} /></Col>
                                <Col span={6}>Idle Cost: <InputNumber value={inputs.idle_cost} onChange={v => setInputs({ ...inputs, idle_cost: v })} /></Col>
                                <Col span={6}>Init. Inv: <InputNumber value={inputs.init_inventory} onChange={v => setInputs({ ...inputs, init_inventory: v })} /></Col>
                                <Col span={6}>Allow Shortage: <Switch checked={inputs.shortage_allowed} onChange={v => setInputs({ ...inputs, shortage_allowed: v })} /></Col>
                            </Row>
                            <Table
                                dataSource={Array.from({ length: periods }).map((_, i) => ({ key: i, period: i + 1 }))}
                                columns={inputColumns}
                                pagination={false}
                                size="small"
                                scroll={{ x: 1000 }}
                            />
                            <Button type="primary" onClick={handleCalculate} loading={loading} style={{ marginTop: 20 }}>
                                Solve & Save
                            </Button>
                        </div>
                    )
                },
                {
                    key: "output", label: "Analysis Results", disabled: !isSaved, children: output && (
                        <div>
                            <Row gutter={16}>
                                <Col span={8}><Statistic title="Total Cost" value={output.total_cost} prefix="$" /></Col>
                                <Col span={8}><Statistic title="Total Supply" value={output.summary.total_supply} /></Col>
                                <Col span={8}><Statistic title="Total Demand" value={output.summary.total_demand} /></Col>
                            </Row>
                            <Divider>Allocation Table</Divider>
                            <Table
                                dataSource={output.allocation_table}
                                columns={Object.keys(output.allocation_table[0] || {}).map(k => ({ title: k.toUpperCase(), dataIndex: k, key: k }))}
                                pagination={false}
                                size="small"
                            />
                        </div>
                    )
                }
            ]} />
        </Card>
    );
};

export default TransportModel;
